/**
 * Team panel service.
 *
 * One licence = one team: the owner (Badacz) plus the sublicences they hand
 * out. `getTeam` 404s for solo accounts, which is how the UI knows not to show
 * the Zespół section at all.
 */

import { API_CONFIG } from '@/config/api'
import type { Edition } from '@/types/auth'
import { baseService } from './baseService'

/**
 * Account package standing — an account-level fact, not a team one, which is
 * why it hangs off `/account/` rather than `/team/`. Reachable at both `/api/`
 * and `/api/v1/` like every other endpoint.
 */

export type TeamRole = 'owner' | 'member'

export interface TeamMember {
  id: number
  user_id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: TeamRole
  joined_at: string
  studies_count: number
}

export interface TeamInvitation {
  id: number
  email: string
  status: 'pending' | 'accepted' | 'revoked'
  created_at: string
  expires_at: string
}

export interface Team {
  id: number
  name: string
  organization: string
  role: TeamRole
  seats: { used: number; limit: number }
  licence: { plan: 'trial' | 'full'; expires_at: string | null; is_active: boolean }
  members: TeamMember[]
  invitations: TeamInvitation[]
}

/** The analysis-minutes package for the current billing period. */
export interface TeamUsage {
  period: { start: string; end: string }
  /** null when no limit is enforced (solo accounts). */
  limit_minutes: number | null
  used_minutes: number
  percent_used: number | null
  is_exhausted: boolean
  per_member: Array<{
    user_id: number | null
    email: string
    full_name: string
    minutes: number
    analyses: number
  }>
  per_project: Array<{ project_id: number | null; name: string; minutes: number }>
  recent: Array<{ at: string; project_name: string; seconds: number; by: string; kind: string }>
  history: Array<{ start: string; end: string; minutes: number }>
}

/**
 * What the account has bought, told in the unit the customer bought it in.
 *
 * FaceWise sells respondents; minutes are our cost unit and never appear here.
 * The pool sits on the account and is spent across every study it owns, so a
 * limit of `null` means nobody has set one — not that the account is empty.
 */
export interface AccountPackage {
  edition: Edition
  respondents: {
    /** null when no package has been set on this account. */
    limit: number | null
    used: number
    /** null whenever `limit` is. */
    remaining: number | null
    percent_used: number | null
    is_exhausted: boolean
  }
}

export interface InvitationPreview {
  email: string
  team_name: string
  organization: string
  invited_by: string
  expires_at: string
  has_account: boolean
}

/** Backend error codes the panel reacts to by name rather than by message. */
export type TeamErrorCode =
  | 'no_team'
  | 'not_team_owner'
  | 'seats_exhausted'
  | 'already_member'
  | 'already_invited'
  | 'already_in_team'
  | 'invalid_email'
  | 'invalid_invitation'
  | 'cannot_remove_owner'
  | 'email_failed'

export class TeamService {
  /** The whole panel in one call; null when the account has no team. */
  async getTeam(): Promise<Team | null> {
    try {
      const response = await baseService.get<Team>(API_CONFIG.ENDPOINTS.TEAM.DETAIL)
      return response.data
    } catch (error) {
      if (isTeamError(error, 'no_team')) return null
      throw error
    }
  }

  async getUsage(): Promise<TeamUsage> {
    const response = await baseService.get<TeamUsage>(API_CONFIG.ENDPOINTS.TEAM.USAGE)
    return response.data
  }

  /** The account's own package, whichever edition it runs. */
  async getAccountPackage(): Promise<AccountPackage> {
    const response = await baseService.get<AccountPackage>(API_CONFIG.ENDPOINTS.ACCOUNTS.PACKAGE)
    return response.data
  }

  async renameTeam(data: { name?: string; organization?: string }): Promise<Team> {
    const response = await baseService.patch<Team>(API_CONFIG.ENDPOINTS.TEAM.DETAIL, data)
    return response.data
  }

  async invite(email: string): Promise<Team> {
    const response = await baseService.post<Team>(API_CONFIG.ENDPOINTS.TEAM.INVITATIONS, { email })
    return response.data
  }

  async revokeInvitation(invitationId: number): Promise<Team> {
    const response = await baseService.delete<Team>(
      API_CONFIG.ENDPOINTS.TEAM.INVITATION_DETAIL(invitationId),
    )
    return response.data
  }

  async removeMember(userId: number): Promise<Team> {
    const response = await baseService.delete<Team>(API_CONFIG.ENDPOINTS.TEAM.MEMBER_DETAIL(userId))
    return response.data
  }

  /** Public — the join page runs this before the invitee has logged in. */
  async previewInvitation(token: string): Promise<InvitationPreview> {
    const response = await baseService.get<InvitationPreview>(
      `${API_CONFIG.ENDPOINTS.TEAM.INVITATION_PREVIEW}?token=${encodeURIComponent(token)}`,
    )
    return response.data
  }

  async acceptInvitation(token: string): Promise<Team> {
    const response = await baseService.post<Team>(API_CONFIG.ENDPOINTS.TEAM.INVITATION_ACCEPT, {
      token,
    })
    return response.data
  }
}

/** Narrow an unknown thrown value to one of our coded backend errors. */
export function isTeamError(error: unknown, code: TeamErrorCode): boolean {
  return getTeamErrorCode(error) === code
}

export function getTeamErrorCode(error: unknown): TeamErrorCode | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code?: TeamErrorCode }).code
  }
  return undefined
}

export const teamService = new TeamService()
